import { memo, useEffect } from 'react';
import { Navigate, Outlet, useMatches, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuthStore } from '@/store/useAuthStore';
import { paths } from '@/shared/config/paths';
import { hasGrade } from '@/shared/config/adminGrade';
import PageLoadingSkeleton from '@/shared/components/layout/PageLoadingSkeleton';
import type { RouteHandle } from '@/routes/types';

// 권한 확인 Guard
const PermissionGuard = () => {
	const { isLoading, admin } = useAuthStore();
	const matches = useMatches();
	const navigate = useNavigate();

	// 등급 제한 라우트 접근 가능 여부 (URL 직접 입력 우회 차단)
	const isGradeAllowed = matches.every(match => {
		const grades = (match?.handle as RouteHandle)?.grades;
		return !grades || hasGrade(admin?.grade, grades);
	});

	useEffect(() => {
		if (isLoading) return;

		// 등급 미달 접근 (아래에서 홈으로 리다이렉트)
		if (!isGradeAllowed) {
			toast.error('해당 페이지에 접근 권한이 없습니다.');
			return;
		}

		if (admin!.grade !== 'MASTER') {
			const matchWithPermission = matches.find(m => (m?.handle as RouteHandle)?.permission);
			const permission = (matchWithPermission?.handle as RouteHandle)?.permission;

			if (permission && !admin!.permissions.includes(permission)) {
				toast.error('해당 페이지에 접근 권한이 없습니다.');
				navigate(-1);
			}
		}
	}, [isLoading, isGradeAllowed, admin, matches, navigate]);

	if (isLoading) {
		return <PageLoadingSkeleton />;
	}

	// 등급 미달 시 홈으로 이동
	if (!isGradeAllowed) {
		return <Navigate to={paths.home.path} replace />;
	}

	return <Outlet />;
};

export default memo(PermissionGuard);
